import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import { Table, TableBody, TableRow, TableCell, makeStyles } from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';
import LoadingSpinner from 'components/LoadingSpinner';
import { Message } from 'components/shared';
import { slugify } from 'utils/string-helpers';
import isEmpty from 'lodash/isEmpty';
import useNavigate from 'hooks/useNavigate';
import { useContextMenu } from 'react-contexify';
import getCellFormat from './utils/getCellFormat';
import { TableHeader } from '../common/components';
import useSorting from '../common/hooks';
import TypographyWithTooltip from '../../../../components/shared/TypographyWithTooltip';

const TABLE_CONTEXT_MENU_ID = 'TABLE_CONTEXT_MENU_ID';

const useStyles = makeStyles(theme => ({
  row: {
    textDecoration: 'none',
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  linkRow: {
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.palette.boxShadow.default,
      backgroundColor: `${theme.palette.primary.main}40`
    },
    '& td': {
      fontSize: theme.customStyles.tableRow.fontSize,
      padding: theme.customStyles.tableRow.padding,
      '&:first-child': {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow
      },
      '&:nth-child(2)': {
        maxWidth: theme.customStyles.nthChild.maxWidth,
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow
      },
      '&:nth-child(3)': {
        overflow: theme.customStyles.cuttingTextCompactMode.overflow,
        whiteSpace: theme.customStyles.cuttingTextCompactMode.whiteSpace,
        textOverflow: theme.customStyles.cuttingTextCompactMode.textOverflow
      },
      '&:not(last-child)': {
        borderLeft: `1px solid ${theme.palette.grey.medium}`
      }
    }
  },
  footer: {
    backgroundColor: theme.palette.grey.medium,
    '& td': {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.customStyles.tableTemf.fontSize,
      padding: theme.customStyles.tableRow.padding
    }
  },
  negative: {
    color: theme.palette.error.main,
    userSelect: 'none'
  },
  title: {
    fontSize: theme.customStyles.tableRow.fontSize
  },
  transparent: {
    backgroundColor: 'transparent'
  },
}));

const TableA = ({ state, setTableExportData, widget, forwardRef }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { show } = useContextMenu();
  const { sortBy, sortOrder, getColumnSorting, onColumnSort } = useSorting();
  const { data: response, isLoading, isError, error } = state;

  const { overlay } = widget;
  const { widgetTitle } = overlay;


  const openInNewTab = url => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getMappedColumns = useCallback(() => {
    if (!response) return []; 
    return response.header.map((item, index) => ({
      index: index + 1,
      key: item ? slugify(item) : 'index',
      title: item,
      isSortable: true
    }));
  }, [response]);

  const getMappedRows = useCallback(() => {
    if (!response) return [];
    const columns = getMappedColumns();
    return response.data.map(row =>
      row.reduce(
        (result, next, index) => {
          const key = columns[index]?.key || 'stocklink' || 'index';
          return { ...result, [key]: next };
        },
        { uniqueKey: nanoid(6) }
      )
    );
  }, [response, getMappedColumns]);

  const getMappedFooterRow = useCallback(() => {
    if (!response) return [];
    const columns = getMappedColumns();
    return response.footer.reduce((result, next, index) => {
      const key = columns[index].key || 'index';
      return { ...result, [key]: next };
    }, {});
  }, [getMappedColumns, response]);

  useEffect(() => {
    if (response) {
      const columns = getMappedColumns();
      const rows = [...getMappedRows(), getMappedFooterRow()];
      setTableExportData({
        title: widgetTitle && slugify(widgetTitle),
        columns,
        rows
      });
    }
  }, [
    response,
    getMappedColumns,
    getMappedRows,
    getMappedFooterRow,
    setTableExportData,
    widgetTitle
  ]);

  if (isError) {
    return <Message type="error" text={error} />;
  }

  if (isLoading || !response) {
    return <LoadingSpinner />;
  }

  function handleContextMenu(e, link) {
    const baseUrl = window.location.origin;
    show(e, {
      id: TABLE_CONTEXT_MENU_ID,
      props: {
        tabHref: `${baseUrl}${link}`
      }
    });
  }

  const columns = getMappedColumns();
  const rows = getMappedRows();
  const footerRow = getMappedFooterRow();

  const renderRow = row => {
    return columns.map(column => {
      if (column.key === 'timeserieslinkid' || column.key === 'timeserieslinktype') {
        return null;
      }

      if (column.key === 'stocklink') {
        return null;
      }
      const { value, classList } = getCellFormat(row[column.key]);
      const key = `${column.key}-${row.uniqueKey}`;
      return (
        <TableCell key={key}>
          <TypographyWithTooltip
            variant="subtitle1"
            className={classes.title}
            gutterBottom
            tooltipProps={{
              value,
              placement: 'right',
              TransitionComponent: Zoom,
              arrow: true
            }}
            
          > 
            <span className={classList.map(c => classes[c]).join(' ')}>{value}</span>
          </TypographyWithTooltip>
        </TableCell>
      );
    });
  };

  const renderBody = () => {
    const sortKey = sortBy || 'index';
    const sortedRows = rows.sort(getColumnSorting(sortKey, sortOrder));
    

    const rowElements = sortedRows.map(row => {
      if (row.timeserieslinkid) {
        return (
          <TableRow
            onClick={() => navigate(`/canned/${row.timeserieslinkid}`)}
            onContextMenu={e => handleContextMenu(e, `/canned/${row.timeserieslinkid}`)}
            key={row.uniqueKey}
            className={classes.linkRow}
          >
            {renderRow(row)}
          </TableRow>
        );
      } else if (row.stocklink) {
        return (
          <TableRow
            onDoubleClick={() => openInNewTab(row.stocklink)}
            key={row.uniqueKey}
            className={classes.linkRow}
          >
            {renderRow(row)}
          </TableRow>
        );
      }
      return (
        <TableRow key={row.uniqueKey} className={classes.row}>
          {renderRow(row)}
        </TableRow>
      );
    });

    const footerRowElement = isEmpty(footerRow) ? (
      <TableRow className={classes.footer} />
    ) : (
      <TableRow className={classes.footer}>{renderRow(footerRow)}</TableRow>
    );

    return (
      <>
        {rowElements}
        {footerRowElement}
      </>
    );
  };

  return (
    <Table ref={forwardRef}>
      <TableHeader columns={columns} onColumnSort={onColumnSort} />
      <TableBody>{renderBody()}</TableBody>
    </Table>
  );
};

TableA.propTypes = {
  state: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    error: PropTypes.string,
    data: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }),
  widget: PropTypes.shape({
    title: PropTypes.string,
    colSpan: PropTypes.number.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dateRangeType: PropTypes.string,
    dateMatchType: PropTypes.number,
    isTitleDisplayed: PropTypes.bool.isRequired,
    isDateDisplayed: PropTypes.bool.isRequired,
    isLegendDisplayed: PropTypes.bool.isRequired,
    isChartTooltipDisplayed: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    masterObjectId: PropTypes.number.isRequired,
    masterObjectType: PropTypes.string,
    masterObjectName: PropTypes.string.isRequired,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired
      })
    ),
    masterObjectTypeId: PropTypes.number,
    overlay: PropTypes.shape({
      widgetTitle: PropTypes.string,
      baseWidget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        widgetType: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired
      }).isRequired
    }).isRequired
  }).isRequired,
  setTableExportData: PropTypes.func,
  forwardRef: PropTypes.shape({})
};

TableA.defaultProps = {
  state: {},
  setTableExportData: null,
  forwardRef: null
};

export default TableA;
